import { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { BsEye, BsEyeSlash } from "react-icons/bs";
import WebService from "../../../Services/WebService";
import HelperService from "../../../Services/HelperService";
import { RootState } from "../../../config/Store";
import { Label } from "../Label/Label";

interface PropData {
  show: boolean;
  onClose: () => void;
}

interface FormValues {
  otp: string;
  new_password: string;
  confirm_password: string;
}

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\W).{8,}$/;
const PASSWORD_RULE_MESSAGE =
  "Password must be at least 8 characters, with one uppercase letter, one lowercase letter, and one special character.";
const RESEND_SECONDS = 60;

const ChangePasswordModal = ({ show, onClose }: PropData) => {
  const userInfoData: any = useSelector<RootState, any>(
    (state: any) => state.userInfoData
  );
  const email: string = userInfoData?.user_info?.email || "";

  const [step, setStep] = useState<1 | 2>(1);
  const [maskedEmail, setMaskedEmail] = useState<string>("");
  const [countdown, setCountdown] = useState<number>(0);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    trigger,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ mode: "onChange" });

  // Reset everything whenever the modal is closed so it always opens on step 1.
  useEffect(() => {
    if (!show) {
      setStep(1);
      setMaskedEmail("");
      setCountdown(0);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      reset();
    }
  }, [show, reset]);

  // Resend OTP countdown (1 tick per second).
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const sendOtp = (buttonId: string) => {
    WebService.postAPI({ action: "password/otp/request", body: {}, id: buttonId })
      .then((res: any) => {
        toast.success(res?.message || "OTP sent to your registered email");
        setMaskedEmail(res?.email || email);
        setCountdown(RESEND_SECONDS);
        setStep(2);
      })
      .catch(() => {
        // 429 / other errors are already toasted by WebService; stay where we are.
      });
  };

  const onChangePassword = (data: FormValues) => {
    WebService.postAPI({
      action: "password/otp/change",
      body: { otp: data.otp, new_password: data.new_password },
      id: "change_pwd_btn",
    })
      .then((res: any) => {
        toast.success(
          res?.message ||
            "Password changed successfully. Please login with your new password.",
        );
        onClose();
        // The old token stays valid for its 7 days, so end the session here and
        // make the user prove the new password.
        setTimeout(() => {
          localStorage.clear();
          window.location.href = "/login";
        }, 1200);
      })
      .catch(() => {});
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton className="modal-header-row">
        <Modal.Title as="h5">Change Password</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {step === 1 && (
          <>
            <p className="mb-4">
              We will send a 6-digit OTP to your registered email{" "}
              <strong className="text-break">{email}</strong>
            </p>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="outline-secondary" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="button"
                id="send_otp_btn"
                className="btn btn-brand-1"
                onClick={() => sendOtp("send_otp_btn")}
              >
                Send OTP
              </Button>
            </div>
          </>
        )}

        {step === 2 && (
          <form onSubmit={handleSubmit(onChangePassword)}>
            <p className="font-14 text-secondary mb-3">
              Enter the OTP sent to <strong className="text-break">{maskedEmail}</strong>. It is
              valid for 10 minutes.
            </p>

            <div className="mb-3">
              <Form.Label>OTP</Form.Label>
              <span className="text-danger">*</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                className="form-control"
                placeholder="Enter 6-digit OTP"
                onKeyPress={HelperService.allowOnlyNumericValue}
                {...register("otp", {
                  required: "Please enter the OTP",
                  pattern: { value: /^\d{6}$/, message: "OTP must be exactly 6 digits" },
                  onChange: (e: any) => {
                    // Strip anything non-numeric that got in via paste.
                    const clean = String(e.target.value || "").replace(/\D/g, "").slice(0, 6);
                    if (clean !== e.target.value) {
                      setValue("otp", clean, { shouldValidate: true });
                    }
                  },
                })}
              />
              {errors.otp && (
                <div className="login-error">
                  <Label title={String(errors.otp.message)} modeError={true} />
                </div>
              )}
              <div className="d-flex justify-content-end mt-1">
                <button
                  type="button"
                  id="resend_otp_btn"
                  className="btn btn-link p-0 font-14 text-brand text-decoration-none"
                  disabled={countdown > 0}
                  onClick={() => sendOtp("resend_otp_btn")}
                >
                  {countdown > 0 ? `Resend OTP in ${countdown}s` : "Resend OTP"}
                </button>
              </div>
            </div>

            <div className="mb-3">
              <Form.Label>New Password</Form.Label>
              <span className="text-danger">*</span>
              <div className="input-group">
                <input
                  type={showNewPassword ? "text" : "password"}
                  className="form-control border-end-0"
                  placeholder="New Password"
                  autoComplete="new-password"
                  {...register("new_password", {
                    required: "Please enter a new password",
                    pattern: { value: PASSWORD_REGEX, message: PASSWORD_RULE_MESSAGE },
                    onChange: () => {
                      if (getValues("confirm_password")) trigger("confirm_password");
                    },
                  })}
                />
                <span
                  className="input-group-text text-secondary bg-white border-start-0 cursor-pointer"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <BsEyeSlash size={16} /> : <BsEye size={16} />}
                </span>
              </div>
              {errors.new_password && (
                <div className="login-error">
                  <Label title={String(errors.new_password.message)} modeError={true} />
                </div>
              )}
            </div>

            <div className="mb-3">
              <Form.Label>Confirm Password</Form.Label>
              <span className="text-danger">*</span>
              <div className="input-group">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className="form-control border-end-0"
                  placeholder="Confirm Password"
                  autoComplete="new-password"
                  {...register("confirm_password", {
                    required: "Please confirm the new password",
                    validate: (value) =>
                      value === getValues("new_password") || "Passwords do not match",
                  })}
                />
                <span
                  className="input-group-text text-secondary bg-white border-start-0 cursor-pointer"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <BsEyeSlash size={16} /> : <BsEye size={16} />}
                </span>
              </div>
              {errors.confirm_password && (
                <div className="login-error">
                  <Label title={String(errors.confirm_password.message)} modeError={true} />
                </div>
              )}
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button variant="outline-secondary" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" id="change_pwd_btn" className="btn btn-brand-1">
                Change Password
              </Button>
            </div>
          </form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ChangePasswordModal;
